import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AsyncLoadingState } from '../../enums';
import { AssessmentAPIResponseInterface } from '../../models';
import { studentAssignmentsToTrackingStatus, TrackingStatusType } from '../../utils/studentAssignmentsToTrackingStatus';
import { getBatchAssignments } from '@/services/assignment/getBatchAssignments';
import { ASSIGNMENT_TYPE, ENTITY_TYPE } from '@/services/assignment/constants';
import { StatusType } from '@/components/Global/DonutChart/DonutChart';

// the expected response from the API
interface AssessmentStatusWidgetResponse extends AssessmentAPIResponseInterface {}

// the state of the widget
interface AssessmentStatusWidgetState {
  loading: AsyncLoadingState;
  data: AssessmentStatusWidgetResponse | null;
  error: string | null;
}

interface FetchAssessmentStatusWidgetDataPayload {
  entityIdList: string[];
  schoolId: number;
  districtId: number;
  currentPeriodId: number;
  entityRoster: any[];
}

// the initial state of the widget
const initialState: AssessmentStatusWidgetState = {
  data: null,
  loading: AsyncLoadingState.LOADING,
  error: null,
};

export interface StudentAssessmentStatus {
  studentId: string;
  firstName: string;
  lastName: string;
  assessmentStatus: StatusType | TrackingStatusType;
  latestActivityId?: string;
  completedDate?: string;
}

// the async thunk to fetch the data from the API
export const fetchAssessmentStatusWidgetData = createAsyncThunk<AssessmentStatusWidgetResponse, FetchAssessmentStatusWidgetDataPayload>(
  'assessmentStatusWidget/fetchAssessmentStatusWidgetData',
  async ({ entityIdList, schoolId, districtId, currentPeriodId, entityRoster }, { rejectWithValue }) => {
    try {

      const assignments = await getBatchAssignments({
        entityIdList,
        entityTypeFilter: ENTITY_TYPE.STUDENT,
        districtId, schoolId,
        assignmentType: [ASSIGNMENT_TYPE.BENCHMARK, ASSIGNMENT_TYPE.PROGRESS_MONITORING],
        limit: 10,
        periodIds: [currentPeriodId]
      });

      if (!assignments) {
        return rejectWithValue('Failed to fetch assignments data');
      }

      const filteredAssignments = assignments.map((studentData: any) => {
        if (!studentData.assignments || studentData.assignments.length === 0) {
          return studentData;
        }

        let relevantAssignments = studentData.assignments;

        relevantAssignments.sort((a: any, b: any) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        return {
          ...studentData,
          assignments: relevantAssignments.slice(0, 1)
        };
      });

      const studentAssessmentStatus = studentAssignmentsToTrackingStatus(filteredAssignments, entityRoster);

      // Initialize counts
      let completeCount = 0;
      let scheduledCount = 0;
      let inProgressCount = 0;
      let underReviewCount = 0;
      let requireActionCount = 0;

      // Iterate over studentAssessmentStatus to calculate counts
      const students = studentAssessmentStatus.map(student => {
        switch (student.assessmentStatus) {
          case TrackingStatusType.ASSIGNED:
            scheduledCount++;
            break;
          case TrackingStatusType.IN_PROGRESS:
            inProgressCount++;
            break;
          case TrackingStatusType.UNDER_REVIEW:
          case TrackingStatusType.COMPLETE:
            underReviewCount++;
            break;
          case TrackingStatusType.ERROR:
          case TrackingStatusType.NEEDS_REVIEW:
            requireActionCount++;
            break;
          case TrackingStatusType.PRE_READER:
          case TrackingStatusType.DONE:
            completeCount++;
            break;
          default:
            break;
        }
        return {
            studentId: student.studentId,
            firstName: student.firstName,
            lastName: student.lastName,
            assessmentStatus: student.assessmentStatus,
            latestActivityId: student.latestActivityId,
            completedDate: student.completedDate
        };
      });
      const totalAssessmentCount = completeCount + scheduledCount + inProgressCount + underReviewCount + requireActionCount;
      //complete percentage is completeCount / the total number taken
      const completePercentage = Math.ceil((completeCount / totalAssessmentCount) * 100);

      // Return the structured response
      return {
        classroomAssessmentStatusData: {
          data: {
            completePercentage,
            totalAssessmentCount,
            completeCount,
            scheduledCount,
            inProgressCount,
            underReviewCount,
            requireActionCount,
            students
          }
        }
      };

    } catch (error: any) {
      console.error('Error fetching assessment status data', error);
      return rejectWithValue(error.message);
    }
  }
);

// the slice for the widget
const assessmentStatusWidgetSlice = createSlice({
  name: 'assessmentStatusWidget',
  initialState,
  reducers: {
    updateAssessmentStatusWidgetData(state, action: PayloadAction<AssessmentStatusWidgetState>) {
      return action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssessmentStatusWidgetData.fulfilled, (state, action: PayloadAction<AssessmentStatusWidgetResponse>) => {
        state.loading = AsyncLoadingState.SUCCESS;
        state.data = action.payload;
      })
      .addCase(fetchAssessmentStatusWidgetData.pending, (state) => {
        state.loading = AsyncLoadingState.LOADING;
      })
      .addCase(fetchAssessmentStatusWidgetData.rejected, (state, action) => {
        state.loading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;
      });
  },
});

// Export the actions
export const { updateAssessmentStatusWidgetData } = assessmentStatusWidgetSlice.actions;
export const selectAssessmentStatusWidgetData = (state: any) => state.assessmentStatusWidget;
export default assessmentStatusWidgetSlice.reducer;